import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { ArrowLeft, Check, Music2, Info } from "lucide-react";

const PACK_LABEL = {
  tarkan: "Tarkan Özel",
  baris_manco: "Barış Manço Özel",
  karisik: "Karışık Şarkılar",
};

export default function CreateGame() {
  const { packKey } = useParams();
  const nav = useNavigate();
  const [songs, setSongs] = useState([]);
  const [selected, setSelected] = useState(new Set()); // song keys
  const [grade, setGrade] = useState("9-10");
  const [t1, setT1] = useState(15);
  const [t2, setT2] = useState(20);
  const [t3, setT3] = useState(20);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/packs/${packKey}/songs`).then((r) => setSongs(r.data)).catch((e) => setErr(formatErr(e)));
  }, [packKey]);

  const totals = useMemo(() => {
    const arr = songs.filter((s) => selected.has(s.key));
    return {
      songs: arr.length,
      idioms: arr.reduce((n, s) => n + s.idiom_count, 0),
      questions: arr.reduce((n, s) => n + s.question_count, 0),
    };
  }, [songs, selected]);

  const toggle = (key) => {
    const s = new Set(selected);
    s.has(key) ? s.delete(key) : s.add(key);
    setSelected(s);
  };
  const selectAll = () => setSelected(new Set(songs.map((s) => s.key)));
  const clearAll = () => setSelected(new Set());

  const submit = async () => {
    if (totals.songs === 0) { setErr("En az bir şarkı seç."); return; }
    setErr(null); setLoading(true);
    try {
      const r = await api.post("/games", {
        pack_key: packKey,
        grade_level: grade,
        song_keys: Array.from(selected),
        idiom_count: totals.idioms,   // informational; backend ignores when song_keys given
        allow_audio_replay: false,
        time_idiom: Number(t1), time_meaning: Number(t2), time_context: Number(t3),
      });
      nav(`/teacher/live/${r.data.code}`);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  const allSelected = songs.length > 0 && selected.size === songs.length;

  return (
    <div className="min-h-screen dark bg-[#070B14] text-white">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link to="/teacher" className="text-zinc-400 hover:text-white inline-flex items-center gap-2 text-sm">
          <ArrowLeft className="h-4 w-4"/> Panel
        </Link>
        <h1 className="mt-6 font-heading text-4xl font-bold">Oyun Ayarları</h1>
        <p className="text-zinc-400 mt-1">Paket: <span className="text-white">{PACK_LABEL[packKey] || packKey}</span></p>

        <div className="mt-8 space-y-8 rounded-3xl border border-white/10 bg-[#0B1020]/70 p-8">
          {/* Sınıf Düzeyi */}
          <div>
            <div className="text-xs font-heading tracking-widest text-cyan-300 mb-3">SINIF DÜZEYİ</div>
            <div className="flex flex-wrap gap-2">
              {["9", "10", "9-10"].map((v) => (
                <button key={v} onClick={() => setGrade(v)} data-testid={`grade-${v}`}
                        className={`px-4 py-2 rounded-full border font-heading transition ${
                          grade === v ? "border-cyan-400 text-cyan-200 bg-cyan-500/10"
                                      : "border-white/10 text-zinc-400 hover:border-white/25"
                        }`}>
                  {v === "9-10" ? "9–10 Karışık" : `${v}. Sınıf`}
                </button>
              ))}
            </div>
          </div>

          {/* Şarkı Seçimi */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-heading tracking-widest text-cyan-300">🎵 ŞARKI SEÇİMİ</div>
              <div className="flex items-center gap-2 text-xs">
                {!allSelected && songs.length > 1 && (
                  <button onClick={selectAll} data-testid="select-all-btn"
                          className="text-cyan-300 hover:text-cyan-200">Tümünü seç</button>
                )}
                {selected.size > 0 && (
                  <button onClick={clearAll} className="text-zinc-500 hover:text-white ml-3">Temizle</button>
                )}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {songs.map((s) => {
                const isOn = selected.has(s.key);
                return (
                  <button key={s.key} onClick={() => toggle(s.key)} data-testid={`song-${s.artist}-${s.song}`}
                    className={`group text-left rounded-2xl border p-5 transition ${
                      isOn
                        ? "border-cyan-400 bg-gradient-to-br from-cyan-500/15 to-violet-500/10 shadow-[0_0_44px_-16px_rgba(6,213,230,0.55)]"
                        : "border-white/10 bg-white/[0.02] hover:border-white/25"
                    }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[10px] font-heading tracking-widest text-zinc-500">{s.artist.toUpperCase()}</div>
                        <div className="mt-1 font-literary italic text-xl">{s.song}</div>
                        <div className="mt-2 text-xs text-zinc-400">
                          <span className="text-white font-heading">{s.idiom_count}</span> deyim ·
                          <span className="ml-1 text-white font-heading">{s.question_count}</span> soru
                        </div>
                      </div>
                      <div className={`h-6 w-6 rounded-full flex items-center justify-center border transition ${
                        isOn ? "bg-cyan-400 border-cyan-300 text-black" : "border-white/25 text-transparent"
                      }`}>
                        <Check className="h-4 w-4" strokeWidth={3} />
                      </div>
                    </div>
                  </button>
                );
              })}

              {songs.length > 1 && (
                <button onClick={() => { allSelected ? clearAll() : selectAll(); }}
                        data-testid="song-all"
                        className={`sm:col-span-2 group text-left rounded-2xl border p-5 transition ${
                          allSelected
                            ? "border-violet-400 bg-violet-500/10 shadow-[0_0_36px_-14px_rgba(167,139,250,0.55)]"
                            : "border-dashed border-white/15 bg-white/[0.02] hover:border-white/30"
                        }`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-heading tracking-widest text-violet-200">TÜMÜNÜ SEÇ</div>
                      <div className="mt-1 font-heading text-lg">Tüm {PACK_LABEL[packKey]?.split(" ")[0] || ""} Şarkıları</div>
                      <div className="mt-1 text-xs text-zinc-400">
                        <span className="text-white font-heading">{songs.reduce((n, s) => n + s.idiom_count, 0)}</span> deyim ·
                        <span className="ml-1 text-white font-heading">{songs.reduce((n, s) => n + s.question_count, 0)}</span> soru
                      </div>
                    </div>
                    <Music2 className="h-5 w-5 text-violet-200"/>
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Info card — replaces "Sesi Tekrar Dinletme" */}
          <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/5 p-5 flex gap-4 items-start">
            <Info className="h-5 w-5 text-cyan-300 shrink-0 mt-0.5"/>
            <div>
              <div className="font-heading text-sm text-cyan-200 tracking-widest">🎧 ŞARKI DİNLEME SİSTEMİ</div>
              <p className="text-zinc-300 text-sm mt-1">
                Her şarkı bir kez dinletilir. Ardından o şarkıda yer alan deyimlerin soruları arka arkaya gelir.
              </p>
            </div>
          </div>

          {/* Timers */}
          <div>
            <div className="text-xs font-heading tracking-widest text-cyan-300 mb-3">SORU SÜRELERİ (SN)</div>
            <div className="grid grid-cols-3 gap-3">
              <TimerField label="🎧 Deyimi Yakala" value={t1} onChange={setT1} testId="t-idiom" />
              <TimerField label="💭 Anlamını Yakala" value={t2} onChange={setT2} testId="t-meaning" />
              <TimerField label="🧠 Bağlamı Yakala" value={t3} onChange={setT3} testId="t-context" />
            </div>
          </div>

          {/* Live summary */}
          <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
            <div className="text-xs font-heading tracking-widest text-zinc-500">SEÇİLEN OYUN</div>
            <div className="mt-2 grid grid-cols-3 gap-4">
              <SummaryStat label="ŞARKI" value={totals.songs} testId="summary-songs"/>
              <SummaryStat label="DEYİM" value={totals.idioms} testId="summary-idioms"/>
              <SummaryStat label="SORU"  value={totals.questions} testId="summary-questions"/>
            </div>
          </div>

          {err && <div className="text-rose-300 text-sm" data-testid="create-error">{err}</div>}
          <button onClick={submit} disabled={loading || totals.songs === 0} data-testid="create-game-btn"
                  className="w-full rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-heading font-bold py-4 text-lg hover:opacity-90 disabled:opacity-40">
            {loading ? "Oluşturuluyor..." : "OYUNU OLUŞTUR"}
          </button>
        </div>
      </div>
    </div>
  );
}

const TimerField = ({ label, value, onChange, testId }) => (
  <label className="block">
    <div className="text-xs text-zinc-500 mb-2">{label}</div>
    <input type="number" min={5} max={120} value={value} onChange={(e) => onChange(e.target.value)}
           data-testid={testId}
           className="w-full rounded-lg bg-black/60 border border-white/10 px-3 py-2 focus:border-cyan-400 focus:outline-none"/>
  </label>
);

const SummaryStat = ({ label, value, testId }) => (
  <div className="text-center">
    <div className="text-[10px] text-zinc-500 tracking-widest">{label}</div>
    <div className="mt-1 font-heading text-3xl font-bold tabular-nums" data-testid={testId}>{value}</div>
  </div>
);
